import InputLabel from '../input-label/InputLabel';
import Input from '../input/Input';
import Alert from '../alert/Alert';
import usePagination from '../../hooks/usePagination';
import useSkeleton from './useSkeleton';
import { DfTableCustomEvent, TableColumnType, TableDataType, TableSortByChangedEventType } from '@danfoss/mosaic-core';
import { DfTable } from '@danfoss/mosaic-react';
import { EXCLUDED_TARGET_TABLE_CLASS_NAME } from '../markdown-wrapper/hooks/useTableSorting/useTableSorting';
import { useCallback, useMemo, useState } from 'react';
import { searchByString, sortItemsWith } from '../../helpers/listsHelpers';
import { getClassNames } from '../../helpers/classHelpers';
import styles from './Table.module.scss';

type TableProps = {
    className?: string;
    dynamicFilter?: React.ReactNode;
    title: string;
    columns: (TableColumnType & { isSearchable?: boolean; })[];
    data?: TableDataType[];
    labels: {
        placeholder: string;
        notFoundMessage: string;
    };
    onSort?: (columnId: string, direction: 'asc' | 'desc') => void;
    pagination?: {
        total?: number;
        onBack?: (page: number) => Promise<void>;
        onNext?: (page: number) => Promise<void>;
        onPageSet?: (page: number) => void;
    };

    showSkeletonWhileLoading?: boolean;
};

const Table = ({ className, dynamicFilter, labels, title, data, columns, pagination, showSkeletonWhileLoading, onSort }: TableProps) => {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<{ columnId: string, isDescending: boolean; }>();
    const hasSearch = useMemo(() => columns.filter(({ isSearchable }) => isSearchable), [columns]);
    const filteredRows = useMemo(() => {
        if (!hasSearch)
            return data ?? [];
        return searchByString(data ?? [], search, true, hasSearch.map(c => c.id));
    }, [hasSearch, data, search]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        return sortItemsWith(filteredRows, row => {
            var value = row[sort.columnId];
            if (value.startsWith('<') && value.endsWith('>')) {
                // If the value is an HTML string, we need to extract the text content
                const tempElement = document.createElement('div');
                tempElement.innerHTML = value;
                value = tempElement.textContent || value;
                return value;
            }
            return value;
        }, sort.isDescending);
    }, [filteredRows, sort]);
    const { currentPageChunk, renderPagination } = usePagination({ data: sortedRows, ...pagination });
    const { isInitialSkeleton, isShowingSkeleton, data: skeletonOrData } = useSkeleton({ active: showSkeletonWhileLoading, data: currentPageChunk.length || search.length ? currentPageChunk : undefined, columns: columns.map(({ id }) => id), maxRowNumber: currentPageChunk.length });

    const handleSortBy = useCallback((event: DfTableCustomEvent<TableSortByChangedEventType>) => {
        const { columnId, isDescending } = event.detail;
        if (onSort)
            onSort(columnId, isDescending ? 'desc' : 'asc');
        else {
            setSort({ columnId, isDescending });
        }
    }, [onSort]);

    if (!columns.length && !data?.length) return null;

    return (
        <div className={className}>
            <InputLabel className={styles.label} label={title} />
            {!!hasSearch.length && <Input className={styles.search} value={search} placeholder={labels.placeholder} onChange={setSearch} />}

            {dynamicFilter}

            <DfTable
                className={getClassNames([styles.table, isShowingSkeleton && styles.skeleton, isInitialSkeleton && styles['initial-skeleton']])}
                tableProps={{ className: `df-table df-table-flex ${EXCLUDED_TARGET_TABLE_CLASS_NAME}` }}
                columns={columns}
                data={skeletonOrData}
                isManualSortBy={!!handleSortBy}
                onSortByChanged={handleSortBy}
            />

            {((!isShowingSkeleton && data !== undefined && !currentPageChunk?.length)) && <Alert className={styles['not-found']} children={labels.notFoundMessage} />}
            {(!isShowingSkeleton && data !== undefined && !!currentPageChunk?.length) && renderPagination()}
        </div>
    );
};

export default Table;